/**
 * Icon layer. The UI uses the Iconsax "Linear" set (matching the product design). Components keep importing from
 * 'lucide-react' — Vite and TypeScript alias that module to this file, which swaps each mapped name for its Iconsax
 * equivalent and falls back to Lucide (drawn at the same 1.5 stroke) for the few glyphs Iconsax lacks.
 * Generated mapping — edit ICON_MAP below to change an icon everywhere.
 */
import { forwardRef, type SVGProps } from 'react';
import {
  Activity as IxActivity,
  Add as IxAdd,
  AddCircle as IxAddCircle,
  Airplane as IxAirplane,
  Archive as IxArchive,
  ArrangeHorizontal as IxArrangeHorizontal,
  ArrowDown as IxArrowDown,
  ArrowLeft as IxArrowLeft,
  ArrowRight as IxArrowRight,
  ArrowSwapVertical as IxArrowSwapVertical,
  ArrowUp as IxArrowUp,
  Bag2 as IxBag2,
  Bank as IxBank,
  Book as IxBook,
  Box as IxBox,
  Briefcase as IxBriefcase,
  Brush2 as IxBrush2,
  Building as IxBuilding,
  Calendar as IxCalendar,
  CalendarAdd as IxCalendarAdd,
  CalendarTick as IxCalendarTick,
  Call as IxCall,
  Camera as IxCamera,
  Card as IxCard,
  Category as IxCategory,
  Chart as IxChart,
  ChemicalGlass as IxChemicalGlass,
  ClipboardTick as IxClipboardTick,
  Clock as IxClock,
  CloseCircle as IxCloseCircle,
  Code1 as IxCode1,
  Coin as IxCoin,
  Copy as IxCopy,
  Cpu as IxCpu,
  Danger as IxDanger,
  Data2 as IxData2,
  DirectInbox as IxDirectInbox,
  Discover as IxDiscover,
  Document as IxDocument,
  DocumentDownload as IxDocumentDownload,
  DocumentForward as IxDocumentForward,
  DocumentText as IxDocumentText,
  DocumentText1 as IxDocumentText1,
  DocumentUpload as IxDocumentUpload,
  DollarCircle as IxDollarCircle,
  Driver as IxDriver,
  Edit2 as IxEdit2,
  Electricity as IxElectricity,
  Element4 as IxElement4,
  EmojiHappy as IxEmojiHappy,
  ExportSquare as IxExportSquare,
  Eye as IxEye,
  EyeSlash as IxEyeSlash,
  Filter as IxFilter,
  Flag as IxFlag,
  Flash as IxFlash,
  Folder2 as IxFolder2,
  Forbidden2 as IxForbidden2,
  Gallery as IxGallery,
  Gift as IxGift,
  Global as IxGlobal,
  Gps as IxGps,
  Grid2 as IxGrid2,
  Hashtag as IxHashtag,
  Headphone as IxHeadphone,
  Health as IxHealth,
  Hierarchy as IxHierarchy,
  Hierarchy2 as IxHierarchy2,
  Home2 as IxHome2,
  Hospital as IxHospital,
  InfoCircle as IxInfoCircle,
  Kanban as IxKanban,
  Key as IxKey,
  Keyboard as IxKeyboard,
  LanguageSquare as IxLanguageSquare,
  Layer as IxLayer,
  Lifebuoy as IxLifebuoy,
  Link2 as IxLink2,
  Lock as IxLock,
  Logout as IxLogout,
  Lovely as IxLovely,
  MagicStar as IxMagicStar,
  Map as IxMap,
  Maximize4 as IxMaximize4,
  Message as IxMessage,
  MessageQuestion as IxMessageQuestion,
  MessageRemove as IxMessageRemove,
  MessageText as IxMessageText,
  Messages2 as IxMessages2,
  Messenger as IxMessenger,
  Microphone2 as IxMicrophone2,
  Minus as IxMinus,
  Mobile as IxMobile,
  Monitor as IxMonitor,
  MonitorRecorder as IxMonitorRecorder,
  Moon as IxMoon,
  More as IxMore,
  Mouse as IxMouse,
  Music as IxMusic,
  Note as IxNote,
  Notification as IxNotification,
  Paperclip2 as IxPaperclip2,
  Pause as IxPause,
  PauseCircle as IxPauseCircle,
  People as IxPeople,
  Play as IxPlay,
  PlayCircle as IxPlayCircle,
  Printer as IxPrinter,
  Profile2User as IxProfile2User,
  Receipt as IxReceipt,
  Refresh as IxRefresh,
  Reserve as IxReserve,
  RotateLeft as IxRotateLeft,
  RotateRight as IxRotateRight,
  Routing2 as IxRouting2,
  RowVertical as IxRowVertical,
  Save2 as IxSave2,
  SearchNormal1 as IxSearchNormal1,
  SearchStatus as IxSearchStatus,
  SearchZoomIn as IxSearchZoomIn,
  SearchZoomOut as IxSearchZoomOut,
  Send2 as IxSend2,
  Setting2 as IxSetting2,
  Setting3 as IxSetting3,
  Setting4 as IxSetting4,
  Shield as IxShield,
  ShieldSecurity as IxShieldSecurity,
  ShieldSlash as IxShieldSlash,
  ShieldTick as IxShieldTick,
  ShoppingCart as IxShoppingCart,
  Shuffle as IxShuffle,
  SidebarRight as IxSidebarRight,
  Sms as IxSms,
  Speedometer as IxSpeedometer,
  Star1 as IxStar1,
  Sun1 as IxSun1,
  Tag as IxTag,
  TaskSquare as IxTaskSquare,
  Teacher as IxTeacher,
  Text as IxText,
  TickCircle as IxTickCircle,
  Ticket as IxTicket,
  Timer1 as IxTimer1,
  Trash as IxTrash,
  TrendDown as IxTrendDown,
  TrendUp as IxTrendUp,
  User as IxUser,
  UserAdd as IxUserAdd,
  Verify as IxVerify,
  Video as IxVideo,
  VolumeHigh as IxVolumeHigh,
  Wifi as IxWifi,
} from '@/lib/iconsax';
import type { Icon as IconsaxIcon } from '@/lib/iconsax';
import type { LucideIcon, LucideProps } from 'lucide-original';
export * from 'lucide-original';

function iconsax(Icon: IconsaxIcon, name: string): LucideIcon {
  const Wrapped = forwardRef<SVGSVGElement, LucideProps>(function Wrapped({ size, color, strokeWidth: _sw, absoluteStrokeWidth: _a, className, ...rest }, ref) {
    return <Icon ref={ref as never} size={size ?? 24} color={color ?? 'currentColor'} variant="Linear" className={className ? `bf-icon ${className}` : 'bf-icon'} {...(rest as SVGProps<SVGSVGElement> as object)} />;
  });
  Wrapped.displayName = name;
  return Wrapped as unknown as LucideIcon;
}

export const Activity = iconsax(IxActivity, 'Activity');
export const AlertCircle = iconsax(IxInfoCircle, 'AlertCircle');
export const AlertTriangle = iconsax(IxDanger, 'AlertTriangle');
export const Archive = iconsax(IxArchive, 'Archive');
export const ArrowDown = iconsax(IxArrowDown, 'ArrowDown');
export const ArrowLeft = iconsax(IxArrowLeft, 'ArrowLeft');
export const ArrowRight = iconsax(IxArrowRight, 'ArrowRight');
export const ArrowRightLeft = iconsax(IxArrangeHorizontal, 'ArrowRightLeft');
export const ArrowUp = iconsax(IxArrowUp, 'ArrowUp');
export const ArrowUpRight = iconsax(IxExportSquare, 'ArrowUpRight');
export const AtSign = iconsax(IxSms, 'AtSign');
export const BadgeCheck = iconsax(IxVerify, 'BadgeCheck');
export const Ban = iconsax(IxForbidden2, 'Ban');
export const BarChart3 = iconsax(IxChart, 'BarChart3');
export const Bell = iconsax(IxNotification, 'Bell');
export const BookOpen = iconsax(IxBook, 'BookOpen');
export const Bot = iconsax(IxMessenger, 'Bot');
export const Boxes = iconsax(IxBox, 'Boxes');
export const Brain = iconsax(IxCpu, 'Brain');
export const Briefcase = iconsax(IxBriefcase, 'Briefcase');
export const Building2 = iconsax(IxBuilding, 'Building2');
export const Calendar = iconsax(IxCalendar, 'Calendar');
export const CalendarClock = iconsax(IxCalendarTick, 'CalendarClock');
export const CalendarPlus = iconsax(IxCalendarAdd, 'CalendarPlus');
export const Camera = iconsax(IxCamera, 'Camera');
export const CheckCircle2 = iconsax(IxTickCircle, 'CheckCircle2');
export const ChevronsUpDown = iconsax(IxArrowSwapVertical, 'ChevronsUpDown');
export const CircleHelp = iconsax(IxMessageQuestion, 'CircleHelp');
export const ClipboardCheck = iconsax(IxClipboardTick, 'ClipboardCheck');
export const Clock = iconsax(IxClock, 'Clock');
export const Coins = iconsax(IxCoin, 'Coins');
export const Compass = iconsax(IxDiscover, 'Compass');
export const Contact = iconsax(IxProfile2User, 'Contact');
export const Copy = iconsax(IxCopy, 'Copy');
export const CreditCard = iconsax(IxCard, 'CreditCard');
export const DollarSign = iconsax(IxDollarCircle, 'DollarSign');
export const Download = iconsax(IxDocumentDownload, 'Download');
export const ExternalLink = iconsax(IxExportSquare, 'ExternalLink');
export const Eye = iconsax(IxEye, 'Eye');
export const EyeOff = iconsax(IxEyeSlash, 'EyeOff');
export const File = iconsax(IxDocument, 'File');
export const FileMinus = iconsax(IxDocumentForward, 'FileMinus');
export const FileText = iconsax(IxDocumentText, 'FileText');
export const FileUp = iconsax(IxDocumentUpload, 'FileUp');
export const Flag = iconsax(IxFlag, 'Flag');
export const Flame = iconsax(IxFlash, 'Flame');
export const FlaskConical = iconsax(IxChemicalGlass, 'FlaskConical');
export const Folder = iconsax(IxFolder2, 'Folder');
export const GalleryHorizontal = iconsax(IxGallery, 'GalleryHorizontal');
export const Gauge = iconsax(IxSpeedometer, 'Gauge');
export const Gift = iconsax(IxGift, 'Gift');
export const GitBranch = iconsax(IxHierarchy, 'GitBranch');
export const GitCompare = iconsax(IxData2, 'GitCompare');
export const GitMerge = iconsax(IxHierarchy2, 'GitMerge');
export const Globe = iconsax(IxGlobal, 'Globe');
export const GraduationCap = iconsax(IxTeacher, 'GraduationCap');
export const Hash = iconsax(IxHashtag, 'Hash');
export const Headphones = iconsax(IxHeadphone, 'Headphones');
export const HeartPulse = iconsax(IxHealth, 'HeartPulse');
export const HelpCircle = iconsax(IxMessageQuestion, 'HelpCircle');
export const History = iconsax(IxClock, 'History');
export const Home = iconsax(IxHome2, 'Home');
export const Image = iconsax(IxGallery, 'Image');
export const Inbox = iconsax(IxDirectInbox, 'Inbox');
export const Info = iconsax(IxInfoCircle, 'Info');
export const KanbanSquare = iconsax(IxKanban, 'KanbanSquare');
export const KeyRound = iconsax(IxKey, 'KeyRound');
export const Keyboard = iconsax(IxKeyboard, 'Keyboard');
export const Landmark = iconsax(IxBank, 'Landmark');
export const Languages = iconsax(IxLanguageSquare, 'Languages');
export const Laptop = iconsax(IxMonitor, 'Laptop');
export const Layers = iconsax(IxLayer, 'Layers');
export const LayoutDashboard = iconsax(IxCategory, 'LayoutDashboard');
export const LifeBuoy = iconsax(IxLifebuoy, 'LifeBuoy');
export const Link2 = iconsax(IxLink2, 'Link2');
export const List = iconsax(IxRowVertical, 'List');
export const ListChecks = iconsax(IxTaskSquare, 'ListChecks');
export const ListFilter = iconsax(IxFilter, 'ListFilter');
export const Lock = iconsax(IxLock, 'Lock');
export const LogOut = iconsax(IxLogout, 'LogOut');
export const Mail = iconsax(IxSms, 'Mail');
export const Map = iconsax(IxMap, 'Map');
export const Maximize = iconsax(IxMaximize4, 'Maximize');
export const Megaphone = iconsax(IxVolumeHigh, 'Megaphone');
export const MessageCircle = iconsax(IxMessage, 'MessageCircle');
export const MessageSquare = iconsax(IxMessage, 'MessageSquare');
export const MessageSquareOff = iconsax(IxMessageRemove, 'MessageSquareOff');
export const MessageSquareText = iconsax(IxMessageText, 'MessageSquareText');
export const MessagesSquare = iconsax(IxMessages2, 'MessagesSquare');
export const Mic = iconsax(IxMicrophone2, 'Mic');
export const Minus = iconsax(IxMinus, 'Minus');
export const Monitor = iconsax(IxMonitor, 'Monitor');
export const MonitorPlay = iconsax(IxMonitorRecorder, 'MonitorPlay');
export const Moon = iconsax(IxMoon, 'Moon');
export const MoreHorizontal = iconsax(IxMore, 'MoreHorizontal');
export const MousePointerClick = iconsax(IxMouse, 'MousePointerClick');
export const Music2 = iconsax(IxMusic, 'Music2');
export const Package = iconsax(IxBox, 'Package');
export const Palette = iconsax(IxBrush2, 'Palette');
export const PanelRight = iconsax(IxSidebarRight, 'PanelRight');
export const Paperclip = iconsax(IxPaperclip2, 'Paperclip');
export const PartyPopper = iconsax(IxLovely, 'PartyPopper');
export const Pause = iconsax(IxPause, 'Pause');
export const PauseCircle = iconsax(IxPauseCircle, 'PauseCircle');
export const Pencil = iconsax(IxEdit2, 'Pencil');
export const Phone = iconsax(IxCall, 'Phone');
export const Plane = iconsax(IxAirplane, 'Plane');
export const Play = iconsax(IxPlay, 'Play');
export const PlayCircle = iconsax(IxPlayCircle, 'PlayCircle');
export const Plug = iconsax(IxElectricity, 'Plug');
export const PlugZap = iconsax(IxFlash, 'PlugZap');
export const Plus = iconsax(IxAdd, 'Plus');
export const PlusCircle = iconsax(IxAddCircle, 'PlusCircle');
export const Printer = iconsax(IxPrinter, 'Printer');
export const Puzzle = iconsax(IxElement4, 'Puzzle');
export const Receipt = iconsax(IxReceipt, 'Receipt');
export const RefreshCw = iconsax(IxRefresh, 'RefreshCw');
export const RotateCcw = iconsax(IxRotateLeft, 'RotateCcw');
export const RotateCw = iconsax(IxRotateRight, 'RotateCw');
export const Save = iconsax(IxSave2, 'Save');
export const ScanSearch = iconsax(IxSearchStatus, 'ScanSearch');
export const ScrollText = iconsax(IxDocumentText1, 'ScrollText');
export const Search = iconsax(IxSearchNormal1, 'Search');
export const Send = iconsax(IxSend2, 'Send');
export const Server = iconsax(IxDriver, 'Server');
export const Settings = iconsax(IxSetting2, 'Settings');
export const Settings2 = iconsax(IxSetting4, 'Settings2');
export const Shield = iconsax(IxShield, 'Shield');
export const ShieldAlert = iconsax(IxShieldSecurity, 'ShieldAlert');
export const ShieldCheck = iconsax(IxShieldTick, 'ShieldCheck');
export const ShieldOff = iconsax(IxShieldSlash, 'ShieldOff');
export const ShoppingBag = iconsax(IxBag2, 'ShoppingBag');
export const ShoppingCart = iconsax(IxShoppingCart, 'ShoppingCart');
export const Shuffle = iconsax(IxShuffle, 'Shuffle');
export const Smartphone = iconsax(IxMobile, 'Smartphone');
export const Smile = iconsax(IxEmojiHappy, 'Smile');
export const Sparkles = iconsax(IxMagicStar, 'Sparkles');
export const Split = iconsax(IxRouting2, 'Split');
export const Star = iconsax(IxStar1, 'Star');
export const Stethoscope = iconsax(IxHospital, 'Stethoscope');
export const StickyNote = iconsax(IxNote, 'StickyNote');
export const Sun = iconsax(IxSun1, 'Sun');
export const Table2 = iconsax(IxGrid2, 'Table2');
export const Tag = iconsax(IxTag, 'Tag');
export const Target = iconsax(IxGps, 'Target');
export const Ticket = iconsax(IxTicket, 'Ticket');
export const Timer = iconsax(IxTimer1, 'Timer');
export const Trash = iconsax(IxTrash, 'Trash');
export const Trash2 = iconsax(IxTrash, 'Trash2');
export const TrendingDown = iconsax(IxTrendDown, 'TrendingDown');
export const TrendingUp = iconsax(IxTrendUp, 'TrendingUp');
export const Type = iconsax(IxText, 'Type');
export const Unplug = iconsax(IxElectricity, 'Unplug');
export const Upload = iconsax(IxDocumentUpload, 'Upload');
export const User = iconsax(IxUser, 'User');
export const UserPlus = iconsax(IxUserAdd, 'UserPlus');
export const Users = iconsax(IxPeople, 'Users');
export const Utensils = iconsax(IxReserve, 'Utensils');
export const Variable = iconsax(IxCode1, 'Variable');
export const Video = iconsax(IxVideo, 'Video');
export const WifiOff = iconsax(IxWifi, 'WifiOff');
export const Workflow = iconsax(IxHierarchy2, 'Workflow');
export const Wrench = iconsax(IxSetting3, 'Wrench');
export const XCircle = iconsax(IxCloseCircle, 'XCircle');
export const Zap = iconsax(IxFlash, 'Zap');
export const ZoomIn = iconsax(IxSearchZoomIn, 'ZoomIn');
export const ZoomOut = iconsax(IxSearchZoomOut, 'ZoomOut');
